import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { BackButton } from '../components/ui/back-button';
import {
  Plus, DollarSign, BarChart3, Target,
  ChevronRight, Edit2, Trash2, Play, Loader2, X,
} from 'lucide-react';

import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { DeleteConfirmationModal } from '../components/ui/delete-confirmation-modal';

import { API_BASE } from '../utils/apiUrl';
const API = API_BASE;

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-800',
  draft: 'bg-blue-100 text-blue-800',
  archived: 'bg-gray-100 text-gray-600',
};

const BudgetStatusBadge = ({ status }) => (
  <Badge className={`${STATUS_STYLES[status] || STATUS_STYLES.draft} border-0 capitalize`}>
    {status}
  </Badge>
);

const getBusinessCurrency = () => {
  try { return JSON.parse(localStorage.getItem('akountfly_business'))?.currency || 'USD'; } catch { return 'USD'; }
};

const formatCurrency = (amount, currency = getBusinessCurrency()) => {
  const symbols = { NGN: '\u20a6', GBP: '\u00a3', USD: '$', EUR: '\u20ac' };
  const sym = symbols[currency] || `${currency || ''} `;
  return `${sym}${Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const BudgetPage = () => {
  const navigate = useNavigate();
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [budgetToDelete, setBudgetToDelete] = useState(null);
  const [deletingBudget, setDeletingBudget] = useState(false);

  const fetchBudgets = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/budgets${params}`);
      // v1 single-statement budgets are retired - only 3-Statement (v2) budgets surface
      setBudgets((response.data || []).filter(b => b.schema_version === 2));
    } catch (error) {
      toast.error('Failed to load budgets');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchBudgets();
  }, [fetchBudgets]);

  const confirmDeleteBudget = async () => {
    if (!budgetToDelete) return;
    setDeletingBudget(true);
    try {
      await axios.delete(`${API}/budgets/${budgetToDelete.id}`);
      toast.success('Budget deleted');
      fetchBudgets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete budget');
    } finally {
      setDeletingBudget(false);
      setBudgetToDelete(null);
    }
  };

  const activeBudgets = budgets.filter(b => b.status === 'active');
  const draftBudgets = budgets.filter(b => b.status === 'draft');

  return (
    <DashboardLayout>
      <div className="p-4 lg:p-8" data-testid="budget-page">
        {/* Branded compact header */}
        <div
          className="relative overflow-hidden rounded-2xl mb-4 px-4 py-3 md:px-5 md:py-3.5"
          style={{ background: 'radial-gradient(120% 130% at 50% 0%, #33499B 0%, #283F8B 45%, #1B2C66 100%)' }}
          data-testid="budget-page-header"
        >
          <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(95,198,197,0.25) 0%, transparent 70%)', filter: 'blur(30px)' }} />
          <div className="relative flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <BackButton desktopOnly fallbackPath="/" variant="hero" className="shrink-0" />
              <div className="min-w-0">
                <p className="text-[9px] font-extrabold tracking-[0.2em] text-[#5FC6C5] mb-0.5 flex items-center gap-1.5">
                  <Target className="w-3 h-3" /> PLANNING
                </p>
                <h1 className="text-lg lg:text-xl font-extrabold tracking-tight text-white leading-tight">Business Planning</h1>
                <p className="text-white/60 text-[11px] lg:text-xs font-medium truncate">Plan your income and expenses to grow your business</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/budget/v2/create')}
              data-testid="create-3statement-budget-btn"
              size="sm"
              className="shrink-0 h-9 rounded-full bg-[#108981] hover:bg-[#0d766a] text-white shadow-md px-3 sm:px-4"
            >
              <Plus className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">New 3-Statement Budget</span>
              <span className="sm:hidden ml-1">New</span>
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {/* Quick Stats - compact KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="budget-kpi-strip">
            {[
              { key: 'active', label: 'Active Budgets', value: activeBudgets.length, icon: Play, tone: 'bg-emerald-50 text-emerald-600', onClick: () => setStatusFilter('active') },
              { key: 'draft', label: 'Drafts', value: draftBudgets.length, icon: Edit2, tone: 'bg-blue-50 text-blue-600', onClick: () => setStatusFilter('draft') },
              { key: 'total', label: 'Total Budgets', value: budgets.length, icon: BarChart3, tone: 'bg-purple-50 text-purple-600', onClick: () => setStatusFilter('') },
              { key: 'revenue', label: 'Planned Revenue', value: formatCurrency(budgets.reduce((sum, b) => sum + (b.kpis?.total_revenue || 0), 0)), icon: DollarSign, tone: 'bg-amber-50 text-amber-600' },
            ].map((k) => {
              const Icon = k.icon;
              return (
                <Card key={k.key} className={`rounded-xl ring-1 ring-black/5 border-0 shadow-sm ${k.onClick ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all' : ''}`} onClick={k.onClick} data-testid={`budget-kpi-${k.key}`}>
                  <CardContent className="p-3 flex items-center gap-2.5">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${k.tone}`}><Icon className="h-4 w-4" /></span>
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 truncate">{k.label}</p>
                      <p className="text-lg font-extrabold text-slate-900 leading-tight truncate">{k.value}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Filter */}
          {statusFilter && (
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Filtering by: {statusFilter}</Badge>
              <Button variant="ghost" size="sm" onClick={() => setStatusFilter('')} data-testid="clear-budget-filter">
                <X className="h-4 w-4" />
                Clear
              </Button>
            </div>
          )}

          {/* Budget List */}
          <Card className="rounded-2xl">
            <CardHeader className="px-4 py-3 sm:px-5">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-base">Your Budgets</CardTitle>
                <Button variant="outline" size="sm" className="h-8 rounded-full" onClick={() => navigate('/budget/vs-actuals')} data-testid="view-vs-actuals-btn">
                  <BarChart3 className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Budget vs Actuals Report</span>
                  <span className="sm:hidden ml-1">vs Actuals</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-3 pb-3 sm:px-5 sm:pb-5">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : budgets.length === 0 ? (
                <div className="text-center py-10 px-4 rounded-xl bg-gradient-to-b from-slate-50 to-white border border-dashed border-slate-200">
                  <span className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-[#108981]/10 flex items-center justify-center"><Target className="h-7 w-7 text-[#108981]" /></span>
                  <h3 className="text-base font-semibold mb-1">No budgets yet</h3>
                  <p className="text-sm text-muted-foreground mb-4 max-w-xs mx-auto">
                    Create your first 3-Statement budget to start planning your finances
                  </p>
                  <Button onClick={() => navigate('/budget/v2/create')} data-testid="empty-state-create-budget-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Budget
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {budgets.map(budget => (
                    <div
                      key={budget.id}
                      className="flex items-center justify-between gap-2 p-3 sm:p-4 border rounded-xl hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/budgets/v2/${budget.id}`)}
                      data-testid={`budget-item-${budget.id}`}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium text-sm sm:text-base truncate">{budget.name}</h3>
                            <BudgetStatusBadge status={budget.status} />
                            {budget.project_name && (
                              <Badge variant="outline" className="text-xs border-indigo-300 text-indigo-700" data-testid={`budget-project-badge-${budget.id}`}>
                                Project: {budget.project_name}{budget.project_mode === 'within' ? ' · within' : budget.project_mode === 'addition' ? ' · addition' : ''}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs sm:text-sm text-muted-foreground">
                            {new Date(budget.period_start).toLocaleDateString()} - {new Date(budget.period_end).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-4 shrink-0">
                        <div className="hidden md:flex items-center gap-4 mr-2">
                          <div className="text-right">
                            <p className="font-medium text-sm">{formatCurrency(budget.kpis?.total_revenue || 0)}</p>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Revenue</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-sm">{formatCurrency(budget.kpis?.total_operating_profit || 0)}</p>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Op Profit</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-sm text-emerald-700">{formatCurrency(budget.kpis?.total_net_profit || 0)}</p>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Net Profit</p>
                          </div>
                        </div>
                        {budget.status !== 'active' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); setBudgetToDelete(budget); }}
                            className="text-red-500 hover:text-red-700"
                            data-testid={`delete-budget-${budget.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <DeleteConfirmationModal
          open={!!budgetToDelete}
          onOpenChange={(open) => !open && setBudgetToDelete(null)}
          title="Delete Budget"
          description="Are you sure you want to delete this budget? All budget lines and data will be permanently removed. This action cannot be undone."
          itemName={budgetToDelete?.name}
          itemType="Budget"
          onConfirm={confirmDeleteBudget}
          loading={deletingBudget}
        />
      </div>
    </DashboardLayout>
  );
};

export default BudgetPage;
